import angular from 'angular'

// @note Specifically add the toast notification class to the template. Without it toasts will fail to appear in some
// instances for Safari.
const TEMPLATE =
  '<vd-toast-notification class="vd-toast-notification-container"></vd-toast-notification>'
const VISIBLE_CLASS = 'vd-visible'
const TIMEOUT = 4000

angular
  .module('vend.ui')
  .service(
    'vdToastNotificationService',
    function ($document, $rootScope, $compile, $timeout, $interval, $q) {
      const ngElement = angular.element
      const notifications = []
      let timer
      let processing
      let $vdToastNotification
      let activeNotification

      this.CATEGORIES = {
        success: 'success',
        negative: 'negative',
      }

      /**
       * Hides the notification.
       *
       * @private
       * @method _hideNotification
       */
      function _hideNotification() {
        const toastNotificationEl = ngElement($vdToastNotification)

        // Wait until the notification has finished it's transition before continuing onto the next
        toastNotificationEl.one('transitionend', () =>
          $rootScope.$applyAsync(() => _continueProcessing())
        )

        toastNotificationEl.removeClass(VISIBLE_CLASS)
      }

      /**
       * Pops the next notification to process off the notifications array. If there are none left, resets the processing
       * state.
       *
       * @private
       * @method _continueProcessing
       */
      function _continueProcessing() {
        if (notifications.length) {
          _processNotification(notifications.shift())
        } else {
          processing = false
        }
      }

      /**
       * Manages the showing and hiding of notifications.
       *
       * @private
       * @method _processNotification
       * @param  notification {Object}
       *         @param notification.message {String}
       *                The message to render in the notification.
       *
       *         @param notification.category {String}
       *                The category of the notification.
       */
      function _processNotification(notification) {
        activeNotification = notification

        ngElement($vdToastNotification).addClass(VISIBLE_CLASS)

        if (timer) {
          $interval.cancel(timer)
        }

        timer = $interval(
          () => _hideNotification(),
          notification.removeDelay,
          1
        )
      }

      /**
       * Renders and compiles the vd-toast-notification directive, and assigns the global $vdToastNotification variable
       * scoped to this service if not already done.
       *
       * @private
       * @method _createToastNotification
       * @return {Promise} Resolves once the directive has been compiled into the page angular app.
       */
      function _createToastNotification() {
        return $q(resolve => {
          // Only create notification if one doesn't already exist.
          if ($vdToastNotification) {
            return resolve()
          }

          const ngAppElements = $document[0].querySelectorAll(
            'html body, [ng-app], [ng-app] body'
          )
          const ngAppElement = ngAppElements[ngAppElements.length - 1]
          const compiledToastContainer = $compile(TEMPLATE)($rootScope)

          ngElement(ngAppElement).append(compiledToastContainer)

          // Without the $timeout, the transition of the first toast notificaiton will not occur
          $timeout(() => {
            $vdToastNotification = compiledToastContainer[0]
            resolve()
          })
        })
      }

      /**
       * The current active notification that is displayed.
       *
       * @property activeNotification
       * @readOnly
       */
      Object.defineProperty(this, 'activeNotification', {
        get() {
          return activeNotification
        },
      })

      /**
       * Opens a toast notification with the passed message and category.
       *
       * @method open
       * @param  params {Object}
       *         @param params.message {String}
       *                The message to render in the notification.
       *
       *         @param params.category {String}
       *                The category of the notification.
       *
       *         @param [params.removeDelay=4000] {Number}
       *                The duration the notification should be shown for.
       *
       *         @param [params.respectLineBreaks=false] {Boolean}
       *                Whether line breaks in the provided message should be respected.
       *
       *         @param [params.displayWarningIcon=false] {Boolean}
       *                Inserts a warning icon into the body of the notification
       */
      this.open = ({
        message,
        category,
        removeDelay = TIMEOUT,
        respectLineBreaks = false,
        displayWarningIcon = false,
      }) => {
        _createToastNotification().then(() => {
          notifications.push({
            message,
            category,
            removeDelay,
            respectLineBreaks,
            displayWarningIcon,
          })

          if (!processing) {
            _continueProcessing()
            processing = true
          }
        })
      }

      /**
       * Opens a success toast notification with the passed message.
       *
       * @method open
       * @param  params {Object}
       *         @param params.message {String}
       *                The message to render in the notification.
       *
       *         @param [params.removeDelay] {Number}
       *                The duration the notification should be shown for.
       *
       *         @param [params.respectLineBreaks] {Boolean}
       *                Whether line breaks in the provided message should be respected.
       */
      this.success = ({ message, removeDelay, respectLineBreaks }) => {
        this.open({
          message,
          category: this.CATEGORIES.success,
          removeDelay,
          respectLineBreaks,
        })
      }

      /**
       * Opens a negative toast notification with the passed message.
       *
       * @method open
       * @param  params {Object}
       *         @param params.message {String}
       *                The message to render in the notification.
       *
       *         @param [params.removeDelay] {Number}
       *                The duration the notification should be shown for.
       *
       *         @param [params.respectLineBreaks] {Boolean}
       *                Whether line breaks in the provided message should be respected.
       *
       *        @param [params.displayWarningIcon] {Boolean}
       *               Inserts a warning icon into the body of the notification.
       */
      this.negative = ({
        message,
        removeDelay,
        respectLineBreaks,
        displayWarningIcon,
      }) => {
        this.open({
          message,
          category: this.CATEGORIES.negative,
          removeDelay,
          respectLineBreaks,
          displayWarningIcon,
        })
      }

      /**
       * Dismisses the notification.
       *
       * @method dismiss
       */
      this.dismiss = () => {
        if (timer) {
          $interval.cancel(timer)
        }

        _hideNotification()
      }
    }
  )
